import * as React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { TRY_MARK_ISSUES_AS_RELATED, tryMarkIssuesAsRelated } from '@/actions/triage';
import { IssueEntity } from '@/types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { IssueCandidatesKind, IssueCandidatesSelect } from './IssueCandidatesSelect';

interface MarkRelatedModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    issue: IssueEntity;
}

export const MarkRelatedModal: React.FC<MarkRelatedModalProps> = ({ open, onClose, orgId, appId, issue }) => {
    const dispatch = useDispatch();
    const [selectedIssueIds, setSelectedIssueIds] = React.useState<string[]>([]);

    const isSaving = useIsLoading(TRY_MARK_ISSUES_AS_RELATED, issue.id);
    const saveError = useError(TRY_MARK_ISSUES_AS_RELATED, issue.id);
    const saveSucceeded = useUpdated(TRY_MARK_ISSUES_AS_RELATED, issue.id);

    React.useEffect(() => {
        if (saveSucceeded) {
            setSelectedIssueIds([]);
            onClose();
        }
    }, [saveSucceeded, onClose]);

    const onSubmit = () => {
        dispatch(tryMarkIssuesAsRelated({ orgId, appId, issueId: issue.id, relatedIssueIds: selectedIssueIds }));
    };

    return (
        <Modal
            title="Mark as related"
            open={open}
            onCancel={onClose}
            onOk={onSubmit}
            okText="Mark as related"
            okButtonProps={{ disabled: selectedIssueIds.length === 0 }}
            confirmLoading={isSaving}
            destroyOnClose
            width={560}
        >
            {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Link this issue to other open issues. Both issues stay independent and show up in each other&apos;s
                related issues.
            </Text>
            <IssueCandidatesSelect
                orgId={orgId}
                appId={appId}
                open={open}
                kind={IssueCandidatesKind.OpenIssues}
                excludeIssueIds={[issue.id, ...(issue.relatedIssueIds ?? [])]}
                value={selectedIssueIds}
                onChange={setSelectedIssueIds}
                placeholder="Select issues"
                notFoundText="No open issues found"
            />
        </Modal>
    );
};
